import type { SectorId, WorkspaceDocument } from '@myplayer/schema';
import { IconCards, IconPlus } from '@myplayer/ui';
import { collection, onSnapshot } from 'firebase/firestore';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
  LinearSpinner,
  Spinner,
  Tag,
  ToastNotification,
} from './components/ui/primitives.js';
import { getFirebaseClients } from './firebase.js';
import { CreateWorkspaceModal, RenameWorkspaceModal, WorkspaceRow } from './workspace-row.js';
import {
  archiveWorkspaceInFirestore,
  createWorkspaceInFirestore,
  deriveWorkspaceIdFromName,
  mapSnapshotDocToWorkspace,
  renameWorkspaceInFirestore,
  slugifyWorkspaceName,
} from './workspaces-firestore.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';

export interface WorkspacesSectionProps {
  readonly initialWorkspaces?: readonly WorkspaceDocument[];
  readonly onSelectWorkspace?: ((ws: WorkspaceDocument) => void) | undefined;
  readonly onCreateWorkspace?: (params: {
    readonly orgId: string;
    readonly workspaceId: string;
    readonly name: string;
    readonly sector: SectorId;
  }) => Promise<WorkspaceDocument>;
  readonly onRenameWorkspace?: (params: {
    readonly orgId: string;
    readonly workspaceId: string;
    readonly name: string;
  }) => Promise<void>;
  readonly onArchiveWorkspace?: (params: {
    readonly orgId: string;
    readonly workspaceId: string;
  }) => Promise<void>;
}

export function WorkspacesSection(props: WorkspacesSectionProps): ReactElement {
  const { session, can, switchWorkspace } = useAuth();
  const canCreateWorkspace = can('workspace:create');
  const canUpdateWorkspace = can('workspace:update');
  const canReadWorkspace = can('workspace:read');

  const isControlled = props.initialWorkspaces !== undefined;
  const [workspaces, setWorkspaces] = useState<readonly WorkspaceDocument[]>(
    props.initialWorkspaces ?? [],
  );
  const [loading, setLoading] = useState<boolean>(!isControlled);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSector, setNewSector] = useState<SectorId>('RETAIL_CPG');
  const [renamingTarget, setRenamingTarget] = useState<WorkspaceDocument | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isControlled) {
      setWorkspaces(props.initialWorkspaces ?? []);
      setLoading(false);
      return;
    }
    if (!session || !canReadWorkspace) {
      setLoading(false);
      return;
    }
    const { db } = getFirebaseClients();
    const colRef = collection(db, 'organisations', session.orgId, 'workspaces');
    return onSnapshot(
      colRef,
      (snap) => {
        const items = snap.docs.map((docSnap) =>
          mapSnapshotDocToWorkspace(
            docSnap.id,
            docSnap.data() as Partial<WorkspaceDocument>,
            session.orgId,
          ),
        );
        items.sort((a, b) => a.workspaceId.localeCompare(b.workspaceId));
        setWorkspaces(items);
        setLoading(false);
      },
      (err) => {
        setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.workspacesActionFallback));
        setLoading(false);
      },
    );
  }, [isControlled, props.initialWorkspaces, session?.orgId, canReadWorkspace]);

  const handleCreateSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!session || !canCreateWorkspace) return;
    setErrorMessage(null);
    setStatusMessage(null);
    const trimmedName = newName.trim();
    if (trimmedName.length === 0) {
      setErrorMessage('Please enter a workspace name.');
      return;
    }
    setSubmitting(true);
    try {
      const workspaceId = deriveWorkspaceIdFromName(trimmedName, session.orgId);
      if (workspaces.some((w) => w.workspaceId === workspaceId)) {
        throw new Error(`Workspace "${workspaceId}" already exists in this organisation.`);
      }
      const created = props.onCreateWorkspace
        ? await props.onCreateWorkspace({
            orgId: session.orgId,
            workspaceId,
            name: trimmedName,
            sector: newSector,
          })
        : await createWorkspaceInFirestore({
            db: getFirebaseClients().db,
            caller: session.callerIdentity,
            orgId: session.orgId,
            workspaceId,
            name: trimmedName,
            sector: newSector,
          });
      if (isControlled) setWorkspaces((prev) => [...prev, created]);
      setNewName('');
      setCreateModalOpen(false);
      setStatusMessage(`Created workspace "${created.name}" (${created.workspaceId}).`);
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.workspacesActionFallback));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveRename = async (ws: WorkspaceDocument): Promise<void> => {
    if (!session || !canUpdateWorkspace) return;
    setErrorMessage(null);
    setStatusMessage(null);
    const trimmed = renameDraft.trim();
    if (trimmed.length === 0) {
      setErrorMessage('Workspace name cannot be empty.');
      return;
    }
    setSubmitting(true);
    try {
      if (props.onRenameWorkspace) {
        await props.onRenameWorkspace({
          orgId: session.orgId,
          workspaceId: ws.workspaceId,
          name: trimmed,
        });
      } else {
        await renameWorkspaceInFirestore({
          db: getFirebaseClients().db,
          caller: session.callerIdentity,
          orgId: session.orgId,
          workspaceId: ws.workspaceId,
          name: trimmed,
        });
      }
      if (isControlled) {
        setWorkspaces((prev) =>
          prev.map((item) =>
            item.workspaceId === ws.workspaceId
              ? {
                  ...item,
                  name: trimmed,
                  slug: slugifyWorkspaceName(trimmed) || ws.workspaceId,
                  updatedBy: session.uid,
                }
              : item,
          ),
        );
      }
      setRenamingTarget(null);
      setRenameDraft('');
      setStatusMessage(`Renamed workspace ${ws.workspaceId} to "${trimmed}".`);
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.workspacesActionFallback));
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async (ws: WorkspaceDocument): Promise<void> => {
    if (!session || !canUpdateWorkspace) return;
    setErrorMessage(null);
    setStatusMessage(null);
    if (ws.workspaceId === session.activeWorkspaceId) {
      setErrorMessage('Switch to another workspace before archiving the active workspace.');
      return;
    }
    setSubmitting(true);
    try {
      if (props.onArchiveWorkspace) {
        await props.onArchiveWorkspace({ orgId: session.orgId, workspaceId: ws.workspaceId });
      } else {
        await archiveWorkspaceInFirestore({
          db: getFirebaseClients().db,
          caller: session.callerIdentity,
          orgId: session.orgId,
          workspaceId: ws.workspaceId,
        });
      }
      if (isControlled) {
        setWorkspaces((prev) =>
          prev.map((item) =>
            item.workspaceId === ws.workspaceId
              ? { ...item, status: 'ARCHIVED', updatedBy: session.uid }
              : item,
          ),
        );
      }
      setStatusMessage(`Archived workspace "${ws.name}" (${ws.workspaceId}).`);
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.workspacesActionFallback));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card data-testid="workspaces-management-card" className="h-full">
      {submitting && <LinearSpinner />}
      <CardHeader>
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <CardIcon>
            <IconCards />
          </CardIcon>
          <CardTitle>Organisation Workspaces</CardTitle>
          <Tag variant="outline" size="sm">
            {workspaces.length}
          </Tag>
        </div>
        {canCreateWorkspace ? (
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={submitting}
              data-testid="open-create-workspace-btn"
              onClick={() => {
                setNewName('');
                setNewSector('RETAIL_CPG');
                setErrorMessage(null);
                setCreateModalOpen(true);
              }}
            >
              <IconPlus />
              <span>Create Workspace</span>
            </Button>
          </div>
        ) : (
          <span
            role="status"
            data-testid="workspaces-readonly-notice"
            className="text-[11px] font-medium text-fg-muted"
          >
            {formatCapabilityRestrictionNotice({
              capability: 'workspace:create',
              actionSummary: 'create, rename, or archive workspaces',
              callerRole: session?.personaRole,
            })}
          </span>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <CardDescription>
          Create, rename, or archive workspaces in your organisation.
        </CardDescription>

        <ToastNotification message={statusMessage} onDismiss={() => setStatusMessage(null)} />
        {errorMessage !== null && !createModalOpen && renamingTarget === null && (
          <p
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {errorMessage}
          </p>
        )}

        {loading ? (
          <div role="status" className="flex items-center gap-2 text-[12px] text-fg-secondary">
            <Spinner size={16} />
            <span>{ADMIN_STRINGS.loading.workspaces}</span>
          </div>
        ) : workspaces.length === 0 ? (
          <p className="text-[12px] text-fg-secondary">{ADMIN_STRINGS.empty.workspaces}</p>
        ) : (
          <div className="overflow-x-auto rounded-control border border-subtle">
            <table className="w-full border-collapse text-left text-[14px]">
              <thead>
                <tr className="border-b border-border bg-surface-muted text-[12px] font-medium text-fg-secondary">
                  <th scope="col" className="px-3.5 py-2.5">
                    Workspace
                  </th>
                  <th scope="col" className="px-3.5 py-2.5">
                    Sector
                  </th>
                  <th scope="col" className="px-3.5 py-2.5">
                    Status
                  </th>
                  {canUpdateWorkspace && (
                    <th scope="col" className="px-3.5 py-2.5 text-right">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-subtle">
                {workspaces.map((ws) => (
                  <WorkspaceRow
                    key={ws.workspaceId}
                    ws={ws}
                    isCurrent={ws.workspaceId === session?.activeWorkspaceId}
                    canUpdateWorkspace={canUpdateWorkspace}
                    submitting={submitting}
                    onSelectWorkspace={
                      props.onSelectWorkspace ?? ((target) => switchWorkspace(target.workspaceId))
                    }
                    onStartRename={(target) => {
                      setRenamingTarget(target);
                      setRenameDraft(target.name);
                      setErrorMessage(null);
                    }}
                    onArchive={(target) => void handleArchive(target)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <CreateWorkspaceModal
        open={createModalOpen}
        orgId={session?.orgId ?? ''}
        newName={newName}
        setNewName={setNewName}
        newSector={newSector}
        setNewSector={setNewSector}
        submitting={submitting}
        errorMessage={errorMessage}
        onClose={() => {
          setCreateModalOpen(false);
          setErrorMessage(null);
        }}
        onSubmit={(e) => void handleCreateSubmit(e)}
      />

      <RenameWorkspaceModal
        target={renamingTarget}
        renameDraft={renameDraft}
        setRenameDraft={setRenameDraft}
        submitting={submitting}
        errorMessage={errorMessage}
        onClose={() => {
          setRenamingTarget(null);
          setRenameDraft('');
          setErrorMessage(null);
        }}
        onSave={(target) => void handleSaveRename(target)}
      />
    </Card>
  );
}
